import { DataProcessingOption } from "models/DataProcesses";
import DirectoryModel from "models/DirectoryModel";
import MontageModel from "models/MontageModel";
import MontageSoundClip from "models/MontageSoundClip";
import moment from "moment";
import BaseService from "./BaseService";

type MontageSaveResponse = {
    montageId: string;
};

export class MontageService extends BaseService {
    protected urlBase: string = "api/Montage";

    private buildMontageFormData(
        requestFieldName: "createRequest" | "updateRequest",
        requestBody: string,
        montageFile: Blob,
        montageSoundClipFiles: Array<{ file: Blob; soundClipId: string }>,
    ): FormData {
        const formData = new FormData();

        formData.append(requestFieldName, requestBody);
        formData.append("montageFile", montageFile);

        montageSoundClipFiles.forEach(({ file }) => {
            formData.append("montageSoundClipFiles", file);
        });

        return formData;
    }

    public async getDirectory(directoryId: string): Promise<DirectoryModel> {
        const params = new URLSearchParams({
            directoryId: directoryId,
        });
        const dir = await this.get<DirectoryModel>(
            new URLSearchParams(params),
            "GetMontageDirectory",
        );

        return dir;
    }
    async getMontageSoundClips(montageId: string) {
        const parms = new URLSearchParams({ montageId });
        const res = await this.get<MontageSoundClip[]>(
            parms,
            "GetSoundClipsByMontageId",
        );
        return res;
    }

    public async listMontages(
        beginDate?: moment.Moment,
        endDate?: moment.Moment,
    ): Promise<MontageModel[]> {
        const params = new URLSearchParams({
            beginDate: beginDate?.format() || "",
            endDate: endDate?.format() || "",
        });
        const rv = await this.get<MontageModel[]>(
            new URLSearchParams(params),
            "List",
        );

        return rv;
    }

    public async downloadMontage(montageId: string): Promise<Blob> {
        const params = new URLSearchParams({ montageId });
        const rv = await this.getBlob(params, "Download");
        return rv;
    }

    public async createMontage(
        name: string,
        directoryName: string,
        clipOrder: string[],
        tones: any[],
        volumes: (number | null)[],
        speeds: (number | null)[],
        montageFile: Blob,
        montageSoundClipFiles: Array<{ file: Blob; soundClipId: string }>,
    ): Promise<MontageSaveResponse> {
        const requestBody = JSON.stringify({
            name: name,
            directoryName: directoryName,
            clipOrder: clipOrder,
            tones: tones.map((t) => JSON.stringify(t)),
            volumes: volumes,
            speeds: speeds,
            montageSoundClipIds: montageSoundClipFiles.map(
                (clip) => clip.soundClipId,
            ),
        });

        const formData = this.buildMontageFormData(
            "createRequest",
            requestBody,
            montageFile,
            montageSoundClipFiles,
        );

        const res: MontageSaveResponse = await this.post(
            formData,
            new URLSearchParams({}),
            "CreateMontage",
        );

        return res;
    }

    public async updateMontage(
        montageId: string,
        clipOrder: string[],
        tones: any[],
        volumes: (number | null)[],
        speeds: (number | null)[],
        montageFile: Blob,
        montageSoundClipFiles: Array<{ file: Blob; soundClipId: string }>,
    ): Promise<MontageSaveResponse> {
        const requestBody = JSON.stringify({
            montageId: montageId,
            clipOrder: clipOrder,
            tones: tones.map((t) => JSON.stringify(t)),
            volumes: volumes,
            speeds: speeds,
            montageSoundClipIds: montageSoundClipFiles.map(
                (clip) => clip.soundClipId,
            ),
        });

        const formData = this.buildMontageFormData(
            "updateRequest",
            requestBody,
            montageFile,
            montageSoundClipFiles,
        );

        const res = await this.post(
            formData,
            new URLSearchParams({}),
            "UpdateMontage",
        );
        return res;
    }

    public async deleteMontages(montageIds: string[]) {
        const urlSearchParams: URLSearchParams = new URLSearchParams();
        for (const montageId of montageIds) {
            urlSearchParams.append("montageIds", montageId);
        }
        const req = await this.delete(urlSearchParams, "DeleteMontages", null);
        return req;
    }

    public async listRedactionServices() {
        const params = new URLSearchParams();

        const res = await this.get(params, "ListRedactionServicesV2");
        return res;
    }

    public async streamRedactedSoundClip(
        voiceRedactionFactor: number,
        orgId: string,
        soundClipId: string,
        dataProcessingOptions: DataProcessingOption[],
    ) {
        const params = new URLSearchParams({ orgId, soundClipId });

        params.append("voiceRedactionFactor", voiceRedactionFactor.toString());

        for (const option of dataProcessingOptions) {
            params.append("dataProcessingOptions", option.processingOption);
        }

        const res = await this.getArrayBuffer(
            params,
            "StreamRedactedSoundClip",
        );
        return res;
    }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new MontageService();
